import partTypeMap from './partTypeMap.json';
import { normalizePartNo } from './ltdEcdUtils';
import { ak0RequisitionKey } from './ak0Utils';
import { getAircraftModex } from './aircraftModex';

const ALL_SITES = 'ALL';

export const lookupPartType = (partNo) => partTypeMap[normalizePartNo(partNo)] || 'Unknown';

const parseDateOnly = (s) => {
    if (!s) return null;
    const str = String(s).slice(0, 10);
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
    if (!m) {
        const d = new Date(str);
        return isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }
    return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
};

export const todayLocal = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

export const formatDisplayDate = (d) => {
    if (!d) return '';
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

const aircraftKey = (site, buno) => `${(site || '').trim()}::${(buno || '').trim()}`;

const siteMatchesFilter = (site, siteFilter) =>
    !siteFilter || siteFilter === ALL_SITES || site === siteFilter;

/** Normalize modex / BUNO suffix so 260, 0260, and 260.0 compare equal. */
export function normalizeModex(value) {
    if (value == null || value === '') return '';
    const s = String(value).trim();
    if (!s) return '';
    const asNum = parseInt(s, 10);
    if (!Number.isNaN(asNum)) return String(asNum);
    return s.replace(/^0+(?=\d)/, '') || s;
}

/** Resolve an AK0 need profile for a fleet aircraft (BUNO vs modex key aliasing). */
export function resolveNeedProfile(fleetEntry, needsByAircraft) {
    if (!fleetEntry || !needsByAircraft) return null;

    const direct = needsByAircraft.get(fleetEntry.key);
    if (direct) return direct;

    const modex = normalizeModex(fleetEntry.modex || fleetEntry.buno?.slice(-3));
    if (modex) {
        const modexKey = aircraftKey(fleetEntry.site, modex);
        const byModex = needsByAircraft.get(modexKey);
        if (byModex) return byModex;
    }

    const fleetBunoNorm = normalizeModex(fleetEntry.buno);
    for (const ac of needsByAircraft.values()) {
        if (ac.site !== fleetEntry.site) continue;
        if (normalizeModex(ac.buno) === fleetBunoNorm) return ac;
        if (modex && normalizeModex(ac.modex) === modex) return ac;
        if (modex && normalizeModex(ac.buno?.slice(-3)) === modex) return ac;
    }

    return null;
}

/**
 * Remaining-life standing of one CRD component.
 *
 * usable: positive hours remaining, or a calendar due date of today or later.
 * expired: zero or negative hours, or a calendar due date before today.
 * unverified: CRD shows the component, but neither hours nor a due date.
 * missing: no CRD life record.
 */
export function donorComponentLifeStatus(life, today = todayLocal()) {
    if (!life) return 'missing';
    if (life.usgRemngQty != null) {
        return life.usgRemngQty > 0 ? 'usable' : 'expired';
    }
    if (life.schdDt) {
        const daysOut = Math.round((life.schdDt.getTime() - today.getTime()) / 86400000);
        return daysOut < 0 ? 'expired' : 'usable';
    }
    return 'unverified';
}

/**
 * Whether a fleet aircraft can act as donor for a specific part.
 *
 * - UP / supply-clear tails (no open AK0) are never cannibalization donors.
 * - Only tails with multiple open AK0 supply lines may donate.
 * - The target part must not be on an open AK0 for the donor.
 * - When CRD All data is loaded, the donor must show that part number
 *   installed in CRD. Presence is not remaining life: an expired component
 *   is not a donor. A row with no life figure can still be suggested, and
 *   the caller must treat it as unverified.
 */
export function isDonorForPart(fleetEntry, partNorm, needsByAircraft, crdLifeByKey = null, today = todayLocal()) {
    const needAc = resolveNeedProfile(fleetEntry, needsByAircraft);

    if (!needAc || needAc.parts.has(partNorm)) {
        return false;
    }

    const missingPartCount = needAc.parts.size;
    const ak0Count = needAc.ak0Count || 0;

    if (missingPartCount === 0 && ak0Count === 0) {
        return false;
    }

    if (!(missingPartCount > 1 || ak0Count > 1)) {
        return false;
    }

    // CRD All is the source of truth for installed components when present.
    if (crdLifeByKey && crdLifeByKey.size > 0) {
        const life = lookupCrdLife(crdLifeByKey, fleetEntry.site, fleetEntry.buno, partNorm);
        if (!life) return false;
        return donorComponentLifeStatus(life, today) !== 'expired';
    }

    return true;
}

/**
 * Build per-aircraft AK0 need profiles from NMCS rows.
 */
export function buildAircraftNeeds(ak0Rows, siteFilter = ALL_SITES) {
    const byAircraft = new Map();

    for (const row of ak0Rows || []) {
        const partNo = (row.part_no || '').toString().trim();
        if (!partNo) continue;

        const site = (row.site_name || '').toString().trim();
        if (!siteMatchesFilter(site, siteFilter)) continue;

        const modex = (row.modex || '').toString().trim();
        const serno = (row.serno || '').toString().trim();
        const buno = serno || modex;
        if (!buno) continue;

        const key = aircraftKey(site, buno);
        let ac = byAircraft.get(key);
        if (!ac) {
            ac = {
                key,
                site,
                buno,
                modex: normalizeModex(modex || (serno ? serno.slice(-3) : '')),
                parts: new Map(),
                requisitions: new Map(),
            };
            byAircraft.set(key, ac);
        }

        const norm = normalizePartNo(partNo);
        let part = ac.parts.get(norm);
        if (!part) {
            part = {
                partNo,
                norm,
                nomen: (row.nomen || '').toString().trim(),
                edd: null,
                partType: lookupPartType(partNo),
            };
            ac.parts.set(norm, part);
        } else if (!part.nomen && row.nomen) {
            part.nomen = row.nomen.toString().trim();
        }

        if (!ac.modex && modex) {
            ac.modex = normalizeModex(modex);
        }

        ac.requisitions.set(ak0RequisitionKey(row), norm);

        const edd = parseDateOnly(row.est_delv_dt);
        if (edd && (!part.edd || edd > part.edd)) {
            part.edd = edd;
        }
    }

    for (const ac of byAircraft.values()) {
        ac.ak0Count = ac.requisitions.size;
        ac.isSingleHit = ac.requisitions.size === 1;
    }

    return byAircraft;
}

/**
 * Fleet aircraft keyed by site::buno from /aircraft/ API.
 */
export function buildFleetIndex(fleetAircraft, siteFilter = ALL_SITES) {
    const byKey = new Map();
    const bySite = new Map();

    for (const ac of fleetAircraft || []) {
        const site = (ac.site_name || '').toString().trim();
        const buno = (ac.buno || '').toString().trim();
        if (!site || !buno) continue;
        if (!siteMatchesFilter(site, siteFilter)) continue;

        const key = aircraftKey(site, buno);
        const entry = {
            key,
            site,
            buno,
            modex: normalizeModex(getAircraftModex(ac) || buno.slice(-3)),
        };
        byKey.set(key, entry);

        let list = bySite.get(site);
        if (!list) {
            list = [];
            bySite.set(site, list);
        }
        list.push(entry);
    }

    for (const list of bySite.values()) {
        list.sort((a, b) => a.buno.localeCompare(b.buno, undefined, { numeric: true }));
    }

    return { byKey, bySite };
}

function daysDownFromLfd(lastFlyByBuno, buno, modex, today) {
    const lfdRaw = lastFlyByBuno[buno] || lastFlyByBuno[modex] || null;
    const lfd = parseDateOnly(lfdRaw);
    if (!lfd) return null;
    return Math.max(0, Math.floor((today.getTime() - lfd.getTime()) / 86400000));
}

function eddDaysOut(edd, today) {
    if (!edd) return null;
    return Math.round((edd.getTime() - today.getTime()) / 86400000);
}

/**
 * Index CRD All rows by site::buno::partNorm for presence + life lookups.
 * Also indexes buno::partNorm (no site) so org-wide CRD uploads still match
 * fleet aircraft when site labeling differs.
 * When multiple component instances exist on a tail, keep the healthiest
 * (most hours remaining, or latest calendar due).
 */
export function buildCrdLifeIndex(crdRows, siteFilter = ALL_SITES) {
    const byKey = new Map();

    for (const row of crdRows || []) {
        const site = (row.site_name || '').toString().trim();
        if (!siteMatchesFilter(site, siteFilter)) continue;

        const buno = (row.buno || '').toString().trim();
        const pn = (row.pn || '').toString().trim();
        if (!buno || !pn) continue;

        const remngRaw = row.usg_remng_qty;
        const parsedRemng = remngRaw == null || remngRaw === ''
            ? null
            : Number(remngRaw);
        const usgRemngQty = (parsedRemng == null || Number.isNaN(parsedRemng)) ? null : parsedRemng;
        const schdDt = parseDateOnly(row.schd_dt);

        const partNorm = normalizePartNo(pn);
        const candidate = {
            site,
            buno,
            partNorm,
            partNo: pn,
            usgRemngQty,
            schdDt,
            blNoIndCd: (row.bl_no_ind_cd || '').toString().trim() || null,
            posCd: (row.pos_cd || '').toString().trim() || null,
        };

        const keys = [
            `${aircraftKey(site, buno)}::${partNorm}`,
            `${normalizeModex(buno)}::${partNorm}`,
        ];

        for (const key of keys) {
            const existing = byKey.get(key);
            if (!existing || compareCrdLife(candidate, existing) > 0) {
                byKey.set(key, candidate);
            }
        }
    }

    return byKey;
}

/** Positive when a is healthier (more remaining life) than b. */
function compareCrdLife(a, b) {
    if (a.usgRemngQty != null && b.usgRemngQty != null) {
        return a.usgRemngQty - b.usgRemngQty;
    }
    if (a.usgRemngQty != null) return 1;
    if (b.usgRemngQty != null) return -1;

    const aMs = a.schdDt ? a.schdDt.getTime() : null;
    const bMs = b.schdDt ? b.schdDt.getTime() : null;
    if (aMs != null && bMs != null) return aMs - bMs;
    if (aMs != null) return 1;
    if (bMs != null) return -1;
    return 0;
}

export function lookupCrdLife(crdLifeByKey, site, buno, partNorm) {
    if (!crdLifeByKey || !buno || !partNorm) return null;
    if (site) {
        const bySite = lookupCrdLifeAtSite(crdLifeByKey, site, buno, partNorm);
        if (bySite) return bySite;
    }
    return crdLifeByKey.get(`${normalizeModex(buno)}::${partNorm}`) || null;
}

/** Site-qualified CRD hit only. A same-BUNO row at another site does not count. */
export function lookupCrdLifeAtSite(crdLifeByKey, site, buno, partNorm) {
    if (!crdLifeByKey || !site || !buno || !partNorm) return null;
    return crdLifeByKey.get(`${aircraftKey(site, buno)}::${partNorm}`) || null;
}

/**
 * Score donor part remaining life. Prefer healthier components so the
 * receiver gets usable hours / calendar life.
 * AFH/EFH rows use usg_remng_qty; CMON/CYR rows use schd_dt.
 */
function scoreDonorPartLife(life, today) {
    if (donorComponentLifeStatus(life, today) !== 'usable') return 0;

    if (life.usgRemngQty != null) {
        const hrs = life.usgRemngQty;
        if (hrs < 50) return 5;
        if (hrs < 200) return 15;
        if (hrs < 500) return 25;
        if (hrs < 1000) return 35;
        return 40 + Math.min(20, Math.floor((hrs - 1000) / 500));
    }

    if (life.schdDt) {
        const daysOut = Math.round((life.schdDt.getTime() - today.getTime()) / 86400000);
        if (daysOut < 0) return 0;
        if (daysOut <= 30) return 5;
        if (daysOut <= 90) return 15;
        if (daysOut <= 180) return 25;
        if (daysOut <= 365) return 35;
        return 45;
    }

    return 0;
}

export function formatDonorLifeDisplay(life) {
    if (!life) {
        return { remngStr: '', schdDtStr: '', summary: '' };
    }
    const remngStr = life.usgRemngQty != null
        ? `${Number(life.usgRemngQty).toFixed(1)} hrs`
        : '';
    const schdDtStr = formatDisplayDate(life.schdDt);
    const summary = remngStr || schdDtStr || '';
    return { remngStr, schdDtStr, summary };
}

function scoreReceiver(ac, part, today) {
    let score = 0;
    if (ac.isSingleHit) score += 100;
    if (ac.ak0Count === 1) score += 20;

    const daysOut = eddDaysOut(part.edd, today);
    if (daysOut == null) score += 40;
    else if (daysOut < 0) score += 60 + Math.min(30, Math.abs(daysOut));
    else if (daysOut <= 30) score += 30;
    else if (daysOut <= 60) score += 15;

    const daysDown = ac.daysDown;
    if (daysDown != null) {
        score += Math.min(40, Math.floor(daysDown / 3));
    }

    return score;
}

export function scoreDonor(fleetEntry, needsByAircraft, partLife = null, today = todayLocal()) {
    const needAc = resolveNeedProfile(fleetEntry, needsByAircraft);
    if (!needAc) return 0;

    let score = 0;
    const missingPartCount = needAc.parts.size;
    const ak0Count = needAc.ak0Count || 0;

    score += Math.min(80, missingPartCount * 15);
    score += Math.min(40, ak0Count * 5);

    if (fleetEntry.daysDown == null) score += 10;
    else score += Math.max(0, 30 - Math.min(30, Math.floor(fleetEntry.daysDown / 5)));

    score += scoreDonorPartLife(partLife, today);

    return score;
}

/**
 * Repairable parts with at least one open AK0 need in scope.
 */
export function getCannibalizationParts(needsByAircraft, { repairableOnly = true } = {}) {
    const partMap = new Map();

    for (const ac of needsByAircraft.values()) {
        for (const part of ac.parts.values()) {
            if (repairableOnly && part.partType !== 'Repairable') continue;

            let entry = partMap.get(part.norm);
            if (!entry) {
                entry = {
                    norm: part.norm,
                    partNo: part.partNo,
                    nomen: part.nomen,
                    partType: part.partType,
                    receiverCount: 0,
                };
                partMap.set(part.norm, entry);
            }
            entry.receiverCount += 1;
            if (!entry.nomen && part.nomen) entry.nomen = part.nomen;
        }
    }

    return Array.from(partMap.values()).sort((a, b) => {
        if (b.receiverCount !== a.receiverCount) return b.receiverCount - a.receiverCount;
        return a.partNo.localeCompare(b.partNo);
    });
}

/**
 * BUNO × Part matrix for a single site.
 */
export function buildCannibalizationMatrix({
    needsByAircraft,
    fleetBySite,
    parts,
    site,
    lastFlyByBuno,
    crdLifeByKey = null,
    today = todayLocal(),
}) {
    const fleet = fleetBySite.get(site) || [];
    const rowKeys = new Set(fleet.map(a => a.key));

    for (const ac of needsByAircraft.values()) {
        if (ac.site === site) rowKeys.add(ac.key);
    }

    const rows = Array.from(rowKeys)
        .map(key => {
            const fleetAc = fleet.find(f => f.key === key);
            const needAc = needsByAircraft.get(key);
            const buno = fleetAc?.buno || needAc?.buno || key.split('::')[1];
            const modex = needAc?.modex || fleetAc?.modex || '';
            return {
                key,
                site,
                buno,
                modex,
                ak0Count: needAc?.ak0Count || 0,
                isSingleHit: needAc?.isSingleHit || false,
                daysDown: daysDownFromLfd(lastFlyByBuno, buno, modex, today),
            };
        })
        .sort((a, b) => a.buno.localeCompare(b.buno, undefined, { numeric: true }));

    const cells = rows.map(row => {
        const fleetEntry = fleet.find(f => f.key === row.key) || { key: row.key, site: row.site, buno: row.buno, modex: row.modex };
        const needAc = resolveNeedProfile(fleetEntry, needsByAircraft);
        return parts.map(part => {
            const need = needAc?.parts.get(part.norm);
            if (need) {
                return {
                    state: 'need',
                    edd: need.edd,
                    eddStr: formatDisplayDate(need.edd),
                    eddDaysOut: eddDaysOut(need.edd, today),
                };
            }
            if (isDonorForPart(fleetEntry, part.norm, needsByAircraft, crdLifeByKey, today)) {
                const partLife = lookupCrdLife(crdLifeByKey, site, row.buno, part.norm);
                return {
                    state: 'donor',
                    usgRemngQty: partLife?.usgRemngQty ?? null,
                    schdDtStr: formatDisplayDate(partLife?.schdDt),
                };
            }
            return { state: 'none' };
        });
    });

    return { rows, parts, cells };
}

/**
 * Ranked cannibalization opportunities.
 * Donor rule: multi-hit tails only, target part not on donor AK0.
 * When CRD All is loaded, donor must have that PN in CRD and the component
 * must not be past its recorded life. Ranking prefers healthier remaining life.
 */
export function buildCannibalizationOpportunities({
    needsByAircraft,
    fleetBySite,
    lastFlyByBuno,
    crdLifeByKey = null,
    siteFilter = ALL_SITES,
    repairableOnly = true,
    singleHitOnly = false,
    today = todayLocal(),
}) {
    const parts = getCannibalizationParts(needsByAircraft, { repairableOnly });
    const sites = siteFilter === ALL_SITES
        ? Array.from(fleetBySite.keys()).sort()
        : [siteFilter];

    const opportunities = [];

    for (const site of sites) {
        const fleet = fleetBySite.get(site) || [];
        if (fleet.length === 0) continue;

        for (const part of parts) {
            const receivers = [];
            for (const ac of needsByAircraft.values()) {
                if (ac.site !== site) continue;
                const need = ac.parts.get(part.norm);
                if (!need) continue;
                if (repairableOnly && need.partType !== 'Repairable') continue;
                if (singleHitOnly && !ac.isSingleHit) continue;

                const enriched = {
                    ...ac,
                    part: need,
                    daysDown: daysDownFromLfd(lastFlyByBuno, ac.buno, ac.modex, today),
                };
                enriched.receiverScore = scoreReceiver(enriched, need, today);
                receivers.push(enriched);
            }

            if (receivers.length === 0) continue;

            const donors = fleet
                .filter(d => isDonorForPart(d, part.norm, needsByAircraft, crdLifeByKey, today))
                .map(d => {
                    const needAc = resolveNeedProfile(d, needsByAircraft);
                    const daysDown = daysDownFromLfd(lastFlyByBuno, d.buno, d.modex, today);
                    const partLife = lookupCrdLife(crdLifeByKey, site, d.buno, part.norm);
                    const lifeDisplay = formatDonorLifeDisplay(partLife);
                    return {
                        ...d,
                        daysDown,
                        ak0Count: needAc?.ak0Count || 0,
                        missingPartCount: needAc?.parts.size ?? 0,
                        partLife,
                        donorUsgRemngQty: partLife?.usgRemngQty ?? null,
                        donorSchdDt: partLife?.schdDt ?? null,
                        donorSchdDtStr: lifeDisplay.schdDtStr,
                        donorLifeStr: lifeDisplay.summary,
                        donorScore: scoreDonor({ ...d, daysDown }, needsByAircraft, partLife, today),
                    };
                })
                .sort((a, b) => b.donorScore - a.donorScore);

            if (donors.length === 0) continue;

            receivers.sort((a, b) => b.receiverScore - a.receiverScore);

            const usedDonors = new Set();
            for (const receiver of receivers) {
                const donor = donors.find(d => !usedDonors.has(d.key));
                if (!donor) break;
                usedDonors.add(donor.key);

                const eddDays = eddDaysOut(receiver.part.edd, today);
                const eddDaysSaved = eddDays == null ? null : Math.max(0, eddDays);

                opportunities.push({
                    id: `${site}::${part.norm}::${receiver.buno}::${donor.buno}`,
                    site,
                    partNorm: part.norm,
                    partNo: part.partNo,
                    nomen: part.nomen || receiver.part.nomen,
                    partType: part.partType,
                    receiverBuno: receiver.buno,
                    receiverAk0Count: receiver.ak0Count,
                    receiverSingleHit: receiver.isSingleHit,
                    receiverDaysDown: receiver.daysDown,
                    receiverEdd: receiver.part.edd,
                    receiverEddStr: formatDisplayDate(receiver.part.edd),
                    receiverEddDaysOut: eddDays,
                    donorBuno: donor.buno,
                    donorAk0Count: donor.ak0Count,
                    donorUsgRemngQty: donor.donorUsgRemngQty,
                    donorSchdDt: donor.donorSchdDt,
                    donorSchdDtStr: donor.donorSchdDtStr,
                    donorLifeStr: donor.donorLifeStr,
                    eddDaysSaved,
                    score: receiver.receiverScore + donor.donorScore,
                });
            }
        }
    }

    opportunities.sort((a, b) => b.score - a.score);
    return opportunities;
}

export function computeCannibalizationStats(opportunities, parts, needsByAircraft) {
    const receiverSet = new Set(opportunities.map(o => `${o.site}::${o.receiverBuno}`));
    const donorSet = new Set(opportunities.map(o => `${o.site}::${o.donorBuno}`));
    const singleHitOps = opportunities.filter(o => o.receiverSingleHit).length;

    return {
        opportunityCount: opportunities.length,
        repairableParts: parts.length,
        receiversMatched: receiverSet.size,
        donorsUsed: donorSet.size,
        singleHitOpportunities: singleHitOps,
        aircraftWithAk0: needsByAircraft.size,
    };
}
